import { css, cx } from '@linaria/core'
import { type FC, useMemo } from 'react'
import type { AppProductGroup, ProductPath } from '#app/types/AppTypes'
import { Icon } from '#components/Icon'
import { fragments, style } from '#styles'
import { theme } from '#styles/themes'
import { testIds } from '#utils/testIds'
import type { ComparisonDisplayMode, ComparisonTableContext } from './ComparisonTableContext'
import type { ConfigurationDiff } from './useConfigurationDifference'
import type { ComparisonDataEntry, GroupDataEntry } from './useLinearizeComparisonData'

const treeClass = css`
  display: flex;
  flex: 1;
  flex-direction: column;
  user-select: none;

  > ._group {
    cursor: pointer;
    padding: 8px 16px 8px 24px;
    align-self: stretch;
    align-items: center;

    display: flex;
    flex-direction: row;
    gap: 6px;

    color: ${style.theme.textAccent};
    ${fragments.textStyle.heading.xxs.regular}

    transition: ease 0.25s background-color;

    > ._title {
      ${fragments.textStyle.body.s.condensedBold}
      letter-spacing: 1px;
    }

    &:hover {
      background: ${theme.backgroundSurfaceStandardSubtle}
    }
  }

  > ._subgroup {
    align-self: stretch;
    cursor: pointer;
    padding: 0px 15px 0px 24px;
    transition: ease 0.25s background-color;
    display: flex; 
    flex-direction: row;
    align-items: center;
    gap: 6px;

    &:hover {
      background: ${theme.backgroundSurfaceStandardSubtle}
    }

    > ._line {
      height: 100%;
      width: 18px;
      background: linear-gradient(${style.theme.textAccent}, ${style.theme.textAccent}) no-repeat center/1px 100%;
    }

    > ._title {
      padding: 8px 0;
      flex: 1;
      color: ${style.theme.textDefault};
      ${fragments.textStyle.body.s.regular}
      line-height: 120%;
    }

    > ._change-count {
      height: 19px;
      aspect-ratio: 1;
      border-radius: 24px;
      color: ${style.theme.textInverse};
      background: ${style.theme.backgroundSurfaceAccentDefault};

      display: flex;
      justify-content: center;
      align-items: center;

      // important: keeps the text center, because the line height might push the glyph up/down
      line-height: 100%;

      > ._value {
        padding: 0px 6px;
      }

    }
  }
`

const GroupEntry: FC<{
  entry: GroupDataEntry
  scrollTo: ComparisonTableContext['virtualizer']['scrollTo']
}> = ({ entry, scrollTo }) => {
  const baseEntry = entry.base ?? entry.comparison
  if (!baseEntry) {
    return undefined
  }

  return (
    <div className="_group" onClick={() => scrollTo(entry)} data-testid={testIds.comparison.sidebarTree.group}>
      <Icon iconUrl={baseEntry.iconUrl} />
      <span className="_title">{baseEntry.title}</span>
    </div>
  )
}

const SubgroupEntry: FC<{
  entry: GroupDataEntry
  scrollTo: ComparisonTableContext['virtualizer']['scrollTo']
  countsByGroupPath: Record<ProductPath, number | undefined>
}> = ({ entry, scrollTo, countsByGroupPath }) => {
  const affectedFamilyCount = countsByGroupPath[entry.id] ?? 0

  const baseEntry = entry.base ?? entry.comparison
  if (!baseEntry) {
    return undefined
  }

  return (
    <div
      className="_subgroup"
      onClick={() => {
        scrollTo(entry)
      }}
      data-testid={testIds.comparison.sidebarTree.subgroup}
    >
      <div className="_line" />
      <span className="_title">{baseEntry.title}</span>
      {affectedFamilyCount > 0 && (
        <div className="_change-count" data-testid={testIds.comparison.changeCount(entry.id)}>
          <span className="_value" data-testid={testIds.comparison.sidebarTree.subgroupCount}>
            {affectedFamilyCount}
          </span>
        </div>
      )}
    </div>
  )
}

export const ComparisonTree: FC<{
  scrollTo: ComparisonTableContext['virtualizer']['scrollTo']
  displayedEntries: ComparisonDataEntry[]
  displayMode: ComparisonDisplayMode
  diff: ConfigurationDiff | undefined
  selectedEntries: Partial<Record<ProductPath, boolean>>
  className?: string
}> = ({ scrollTo, displayedEntries, className }) => {
  const countsByGroupPath = useMemo(() => {
    const counts: Record<ProductPath, number | undefined> = {}

    let activeGroup: AppProductGroup | undefined
    for (const entry of displayedEntries) {
      switch (entry.type) {
        case 'group': {
          activeGroup = entry.base ?? entry.comparison
          break
        }
        case 'family': {
          if (activeGroup && (entry.affected || entry.missing)) {
            if (activeGroup.parentGroup) {
              counts[activeGroup.parentGroup.path] = (counts[activeGroup.parentGroup.path] ?? 0) + 1
            }
            counts[activeGroup.path] = (counts[activeGroup.path] ?? 0) + 1
          }
        }
      }
    }

    return counts
  }, [displayedEntries])

  return (
    <div className={cx(treeClass, className)} data-testid={testIds.comparison.sidebarTree.element}>
      {displayedEntries.map((entry) => {
        switch (entry.type) {
          case 'family':
            return undefined
          case 'group': {
            // @TODO: investigate alternative for type coercion, because one of the two _has to_ exists
            const group = entry.base ?? entry.comparison
            if (!group) {
              return undefined
            }

            return group.parentGroup ? (
              <SubgroupEntry key={entry.id} entry={entry} scrollTo={scrollTo} countsByGroupPath={countsByGroupPath} />
            ) : (
              <GroupEntry key={entry.id} entry={entry} scrollTo={scrollTo} />
            )
          }
        }
        return undefined
      })}
    </div>
  )
}
